import { Box, Checkbox, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { CompactPagination } from 'src/components/organisms/compact-pagination';
import { useAssignEmployeesToEmployeeGroup } from 'src/features/employees-groups/api/hooks/employees/use-assign-employees-to-employee-group';
import { fetchEmployees } from 'src/features/employees/api/lib/employees';
import { employeesKeys } from 'src/features/employees/api/query-keys/employees';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { usePagination } from 'src/hooks/use-pagination';
import { useSearchState } from 'src/hooks/use-search-state';
import { useUserStore } from 'src/store/user-store';

interface Props extends DialogActions {
  employeeGroupToken: string;
}

export const AddEmployeesToGroupDialog = ({
  open,
  onCancel,
  employeeGroupToken,
}: Props) => {
  const { t } = useTranslation('employeesGroups');

  const { directoryToken } = useUserStore();

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const [selectedEmployeesTokens, setSelectedEmployeesTokens] = useState<
    string[]
  >([]);

  const [searchState, setSearchState] = useSearchState({
    q: '',
    filter: ['employeeGroupToken NOT EXISTS OR employeeGroupToken IS NULL'],
  });

  const { results, isLoading, isFetching, error } = useMeilisearch({
    fetch: fetchEmployees,
    page,
    perPage,
    queryKey: employeesKeys.list(
      directoryToken ?? '',
      perPage,
      page,
      searchState,
    ),
    searchState,
    enabled: open,
  });

  useEffect(() => {
    if (!results) return;
    setTotalItems(results.estimatedTotalHits);
  }, [results]);

  const handleOnCancel = () => {
    onCancel?.();
    setSelectedEmployeesTokens([]);
  };

  const {
    mutate: assignEmployeesToEmployeeGroup,
    isPending: isAssigningEmployeesToEmployeeGroup,
  } = useAssignEmployeesToEmployeeGroup(handleOnCancel);

  const handleUpdateSelectedEmployeesTokens = (token: string) => {
    setSelectedEmployeesTokens(prev => {
      if (prev.includes(token)) {
        return prev.filter(item => item !== token);
      }

      return [...prev, token];
    });
  };

  const handleChangeQuery = (query: string) => {
    setSearchState(prev => ({ ...prev, q: query }));
  };

  const handleAssignEmployeesToEmployeeGroup = () => {
    assignEmployeesToEmployeeGroup({
      employeeGroupToken,
      employeesTokens: selectedEmployeesTokens,
      bookingsDecisions: {
        cancelBookingDecisions: [],
        keepBookingUnchangedDecisions: [],
        moveBookingDecisions: [],
      },
      bookingsDecisionsReason: '',
    });
  };

  return (
    <Dialog
      title={t('employees.addEmployeesToGroupDialog.title')}
      open={open}
      onCancel={handleOnCancel}
      onConfirm={handleAssignEmployeesToEmployeeGroup}
      isConfirmButtonSubmitting={isAssigningEmployeesToEmployeeGroup}
      isConfirmButtonDisabled={selectedEmployeesTokens.length === 0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2">
          {t('employees.addEmployeesToGroupDialog.selectEmployees')}
        </Typography>

        <Query
          searchAsType
          disabled={false}
          onChange={(newQuery: string) => {
            handleChangeQuery(newQuery);
          }}
          sx={{
            minWidth: 200,
          }}
          value={searchState.q}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: 2,
          }}>
          {results?.hits?.map(employee => (
            <CardWithContent
              key={employee.token}
              isSelected={selectedEmployeesTokens.includes(employee.token)}
              onClick={() => {
                handleUpdateSelectedEmployeesTokens(employee.token);
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={selectedEmployeesTokens.includes(employee.token)}
                />
                <Typography variant="body2">{employee.email}</Typography>
              </Box>
            </CardWithContent>
          ))}

          <DataStatusIndicator
            dataLength={results?.hits?.length ?? 0}
            isFetching={isLoading}
            error={error?.response?.data?.message}
            resourceUnavailableText={t(
              'employees.addEmployeesToGroupDialog.noEmployees',
            )}
          />
        </Box>

        <CompactPagination
          disabled={isLoading}
          isFetching={isFetching}
          page={page}
          changePage={changePage}
          handlePerPageChange={changePerPage}
          nextDisabled={nextDisabled}
          nextPage={nextPage}
          prevDisabled={prevDisabled}
          prevPage={prevPage}
          perPage={perPage}
          totalPages={totalPages}
        />
      </Box>
    </Dialog>
  );
};
